<template>
  <div class="product-detail-page app-page">
    <el-card v-loading="loading" class="product-detail-card" shadow="never">
      <div v-if="error" class="error-message">{{ error }}</div>

      <div v-if="product && !error" class="product-content">
        <!-- 商品图片与基本信息 -->
        <div class="product-main">
          <div class="product-images">
            <el-image
              :src="product.mainImage"
              alt="商品图片"
              fit="contain"
              class="main-image"
            ></el-image>
          </div>

          <div class="product-info">
            <h1 class="product-name">{{ product.productName }}</h1>
            <div class="product-price">
              <span class="current-price">¥{{ product.price.toFixed(2) }}</span>
            </div>

            <div class="product-stock" v-if="product.stock > 0">
              <div class="stock-icon">
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
              </div>
              <span>库存：{{ product.stock }}件</span>
            </div>
            <div class="product-stock out-of-stock" v-else>
              <div class="stock-icon">
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
              </div>
              <span>库存：无库存</span>
            </div>

            <div class="product-description">
              <div class="desc-header">
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>
                <h3>商品描述</h3>
              </div>
              <p>{{ product.description || '暂无商品描述' }}</p>
            </div>

            <div class="buy-actions">
              <el-button
                type="primary"
                size="large"
                @click="handleBuyNow"
                :disabled="product.stock <= 0"
                class="buy-now-btn"
              >
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 002 1.61h9.72a2 2 0 002-1.61L23 6H6"/></svg>
                立即下单
              </el-button>
              <el-button
                type="success"
                size="large"
                @click="addToCart(product.id)"
                :disabled="product.stock <= 0"
                class="add-cart-btn"
              >
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 5v14"/><path d="M5 12h14"/></svg>
                加入购物车
              </el-button>
            </div>
          </div>
        </div>
      </div>
    </el-card>

    <!-- 地址选择弹窗 -->
    <el-dialog
      v-model="addressDialogVisible"
      title="选择收货地址"
      width="700px"
      :before-close="handleDialogClose"
      class="address-dialog"
    >
      <!-- 简化地址列表结构 -->
      <div class="address-list">
        <el-radio-group v-model="selectedAddressId" v-if="addresses.length > 0">
          <!-- 每条地址直接作为radio项 -->
          <el-radio
            v-for="addr in addresses"
            :key="addr.id"
            :label="addr.id"
            class="address-item"
          >
            <div class="address-info">
              <div class="address-header">
                <span class="receiver">{{ addr.receiver }}</span>
                <span class="phone">{{ addr.phone }}</span>
                <el-tag v-if="addr.isDefault" size="small" class="default-tag">默认</el-tag>
              </div>
              <div class="address-content">
                {{ addr.addressDetail || '未填写详细地址' }}
              </div>
            </div>
          </el-radio>
        </el-radio-group>

        <div v-else class="no-address">
          暂无收货地址，请先添加
        </div>
      </div>

      <template #footer>
        <el-button @click="addressDialogVisible = false">取消</el-button>
        <el-button
          type="primary"
          @click="confirmAddressSelection"
          :disabled="!selectedAddressId && addresses.length > 0"
        >
          确定
        </el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ElMessage, ElMessageBox } from 'element-plus'
import productApi from '@/api/product'
import orderApi from '@/api/order'
import addressApi from '@/api/address'
import cartApi from '@/api/cart'

// 路由实例
const route = useRoute()
const router = useRouter()

// 状态管理
const loading = ref(true)
const error = ref('')
const product = ref(null)
const productId = ref(route.params.id || '')

// 地址相关状态
const addresses = ref([])
const selectedAddressId = ref('')
const addressDialogVisible = ref(false)
const addressLoading = ref(false)

// 监听弹窗显示状态，用于调试
watch(addressDialogVisible, (newVal) => {
  console.log('地址弹窗显示状态:', newVal)
  if (newVal && addresses.value.length > 0) {
    console.log('当前地址列表:', addresses.value)
  }
})

// 获取商品详情
const fetchProductDetail = async () => {
  if (!productId.value) {
    error.value = '商品ID不存在'
    loading.value = false
    return
  }

  try {
    loading.value = true
    const res = await productApi.getProductById(productId.value)

    if (res.data && res.data.code == 200) {
      product.value = res.data.data
    } else {
      error.value = res.data?.msg || '获取商品详情失败'
      ElMessage.error(error.value)
    }
  } catch (err) {
    console.error('获取商品详情失败:', err)
    error.value = '获取商品详情失败，请稍后重试'
    ElMessage.error(error.value)
  } finally {
    loading.value = false
  }
}

// 获取地址列表
const fetchAddressList = async (userId) => {
  try {
    addressLoading.value = true
    const res = await addressApi.getAddressList(userId)

    if (res.data && res.data.code == 200) {
      addresses.value = res.data.data || []
      console.log('获取到的地址列表:', addresses.value)

      // 确保地址列表有数据时才设置选中状态
      if (addresses.value.length > 0) {
        const defaultAddress = addresses.value.find(addr => addr.isDefault)
        selectedAddressId.value = defaultAddress ? defaultAddress.id : addresses.value[0].id
      }
      return addresses.value
    } else {
      ElMessage.error(res.data?.msg || '获取地址列表失败')
      return []
    }
  } catch (err) {
    console.error('获取地址列表失败:', err)
    ElMessage.error('获取地址列表失败，请稍后重试')
    return []
  } finally {
    addressLoading.value = false
  }
}

// 立即下单
const handleBuyNow = async () => {
  const userId = localStorage.getItem('userId')
  if (!userId) {
    ElMessage.warning('请先登录')
    router.push('/login')
    return
  }

  if (!product.value) return

  // 获取地址列表
  const addressList = await fetchAddressList(Number(userId))

  if (addressList.length === 0) {
    ElMessage.warning('请先添加收货地址')
    return
  }

  // 询问是否使用默认地址
  const defaultAddress = addressList.find(addr => addr.isDefault)
  if (defaultAddress) {
    ElMessageBox.confirm(
      '是否使用默认地址？',
      '选择收货地址',
      {
        confirmButtonText: '是',
        cancelButtonText: '否',
        type: 'info',
        closeOnClickModal: false,
        closeOnPressEscape: false
      }
    )
    .then(() => {
      // 用户点击"是"，使用默认地址
      submitOrder(Number(userId), defaultAddress)
    })
    .catch(() => {
      // 用户点击"否"，显示地址列表弹窗
      console.log('用户选择不使用默认地址，显示地址列表')
      if (addresses.value.length > 0) {
        addressDialogVisible.value = true
      } else {
        ElMessage.error('地址数据加载失败，请重试')
      }
    })
  } else {
    // 没有默认地址，直接显示地址列表
    addressDialogVisible.value = true
  }
}

// 确认地址选择
const confirmAddressSelection = () => {
  const userId = localStorage.getItem('userId')
  if (!userId) return

  // 找到选中的地址
  const selectedAddress = addresses.value.find(addr => addr.id === selectedAddressId.value)
  if (selectedAddress) {
    addressDialogVisible.value = false
    submitOrder(Number(userId), selectedAddress)
  } else {
    ElMessage.warning('请选择收货地址')
  }
}

// 提交订单
const submitOrder = async (userId, address) => {
  try {
    const res = await orderApi.createOrder({
      userId: userId,
      items: [
        {
          productId: product.value.id, // 原productId迁移至此
          count: 1, // 原count迁移至此
          price: product.value.price
        }
      ],
      isSeckill: 0,
      address: address.addressDetail,
      receiver: address.receiver,
      phone: address.phone
    })

    if (res.data && res.data.code == 200) {
      const orderSn = res.data.data
      ElMessage.success('订单创建成功，即将跳转到订单详情页面')
      router.push(`/order/detail/${orderSn}`)
    } else {
      ElMessage.error(res.data?.msg || '创建订单失败')
    }
  } catch (err) {
    console.error('创建订单失败:', err)
    ElMessage.error('创建订单失败，请稍后重试')
  }
}

// 加入购物车
const addToCart = async (productId) => {
  try {
    const userId = localStorage.getItem('userId')
    if (!userId) {
      ElMessage.warning('请先登录')
      router.push('/login')
      return
    }

    const res = await cartApi.addToCart({
      userId: Number(userId),
      productId,
      quantity: 1
    }, {
      params: { userId: Number(userId) } // 补充userId作为请求参数（根据后端接口要求）
    })

    if (res.data?.code == 200) {
      // 关键修改：使用confirm方法并指定类型
      ElMessageBox.confirm(
        '商品已加入购物车',
        '操作成功',
        {
          confirmButtonText: '去购物车',
          cancelButtonText: '继续购物',
          type: 'success', // 指定为成功类型
          distinguishCancelAndClose: true // 区分取消和关闭
        }
      ).then(() => {
        // 点击"去购物车"
        router.push('/cart')
      }).catch(action => {
        // 点击"继续购物"或关闭弹窗
        if (action === 'cancel') {
          // 继续购物无需操作
        }
      })
    } else {
      ElMessage.error(res.data?.msg || '加入购物车失败')
    }
  } catch (err) {
    console.error('加入购物车失败:', err)
    ElMessage.error('加入购物车失败')
  }
}


// 弹窗关闭回调
const handleDialogClose = () => {
  console.log('地址弹窗被关闭')
  addressDialogVisible.value = false
}

// 页面加载时获取商品详情
onMounted(() => {
  fetchProductDetail()
})
</script>

<style scoped>
.product-detail-page {
  max-width: 1000px;
  margin: 0 auto;
}

.product-detail-card {
  margin-top: 0;
  border: 1px solid var(--app-border-subtle);
  border-radius: var(--app-radius-xl);
  box-shadow: var(--app-shadow-card);
  overflow: hidden;
}

.error-message {
  color: #f56c6c;
  text-align: center;
  padding: 20px;
}

.product-content {
  padding: 24px;
}

.product-main {
  display: flex;
  gap: 32px;
  align-items: flex-start;
}

.product-images {
  flex: 0 0 420px;
  border-radius: var(--app-radius-lg);
  overflow: hidden;
  background: var(--app-bg-soft);
  border: 1px solid var(--app-border-light);
}

.main-image {
  width: 100%;
  height: 420px;
  background: var(--app-bg-soft);
}

.product-info {
  flex: 1;
  min-width: 0;
}

.product-name {
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 16px;
  color: var(--app-text-primary);
  line-height: 1.4;
}

.product-price {
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--app-border-light);
}

.current-price {
  font-size: 2rem;
  font-weight: 800;
  color: var(--app-color-primary);
}

.product-stock {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 20px;
  color: var(--app-text-secondary);
  font-size: 0.9375rem;
}

.stock-icon {
  color: var(--app-text-muted);
  display: flex;
}

.product-stock.out-of-stock {
  color: #f56c6c;
}

.product-stock.out-of-stock .stock-icon {
  color: #f56c6c;
}

.product-description {
  margin-bottom: 24px;
  padding: 18px 20px;
  background: var(--app-bg-soft);
  border-radius: var(--app-radius-lg);
  border: 1px solid var(--app-border-subtle);
}

.desc-header {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
}

.desc-header svg {
  color: var(--app-color-primary);
}

.desc-header h3 {
  font-size: 0.9375rem;
  font-weight: 600;
  color: var(--app-text-primary);
  margin: 0;
}

.product-description p {
  margin: 0;
  color: var(--app-text-secondary);
  font-size: 0.875rem;
  line-height: 1.7;
}

.buy-actions {
  display: flex;
  gap: 12px;
}

.buy-now-btn {
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 14px 24px !important;
  font-size: 1.0625rem !important;
  font-weight: 600 !important;
  border-radius: var(--app-radius-lg) !important;
  background: var(--app-gradient-primary) !important;
  border: none !important;
  box-shadow: 0 4px 15px rgba(255, 126, 126, 0.35) !important;
}

.buy-now-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(255, 126, 126, 0.5) !important;
}

.add-cart-btn {
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 14px 24px !important;
  font-size: 1.0625rem !important;
  font-weight: 600 !important;
  border-radius: var(--app-radius-lg) !important;
  background: var(--app-bg-elevated) !important;
  border: 2px solid var(--app-color-primary) !important;
  color: var(--app-color-primary) !important;
}

.add-cart-btn:hover:not(:disabled) {
  background: var(--app-color-primary-soft) !important;
  transform: translateY(-2px);
}

/* Address dialog */
:deep(.address-dialog .el-dialog__body) {
  padding: 20px 24px;
}

.address-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.address-item {
  display: flex;
  align-items: flex-start;
  width: 100%;
  padding: 16px !important;
  border: 1px solid var(--app-border-light) !important;
  border-radius: var(--app-radius-lg) !important;
  transition: all 0.2s ease;
  height: auto !important;
  margin-bottom: 0 !important;
}

.address-item:hover {
  border-color: var(--app-color-primary-light) !important;
  background: var(--app-color-primary-muted) !important;
}

:deep(.address-item .el-radio__label) {
  width: 100%;
}

.address-info {
  width: 100%;
}

.address-header {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
  margin-bottom: 8px;
}

.receiver {
  font-weight: 600;
  color: var(--app-text-primary);
}

.phone {
  color: var(--app-text-secondary);
  font-size: 0.875rem;
}

.default-tag {
  border-radius: var(--app-radius-full);
  padding: 0 8px;
}

.address-content {
  color: var(--app-text-secondary);
  font-size: 0.875rem;
  line-height: 1.5;
}

:deep(.el-radio__input.is-checked ~ .address-info) {
  color: var(--app-color-primary);
}

.no-address {
  text-align: center;
  padding: 40px 0;
  color: var(--app-text-muted);
}

@media (max-width: 768px) {
  .product-main {
    flex-direction: column;
  }

  .product-images {
    flex: none;
    width: 100%;
  }

  .main-image {
    height: 280px;
  }

  .buy-actions {
    flex-direction: column;
  }
}
</style>
